import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Row,
  Col,
  Spinner,
  Alert,
  Badge,
  Collapse,
} from 'react-bootstrap';
import api from '../../services/api';
import { useHighlightedStores } from '../../context/HighlightedStoresContext';
import { useLanguage } from '../../context/LanguageContext';
import AdminStoreSearchCombobox from '../Admin/AdminStoreSearchCombobox';

const PAGE_SIZE_OPTIONS = [50, 100, 200, 500];

const BILLING_TYPE_OPTIONS = [
  { value: '', label: 'Все типы' },
  { value: 'storage_daily', label: 'Хранение за обработку заказа на Flydarin' },
  { value: 'storage_warehouse', label: 'Хранение за наличие товара на нашем складе при продаже на МП' },
  { value: 'prepare_return_to_seller', label: 'Отправка товара обратно продавцу' },
  { value: 'disposal', label: 'Утилизация товара' },
  { value: 'packaging_shipment', label: 'Отгрузка товара покупателю' },
  { value: 'inspection_no_media', label: 'Проверка товара' },
  { value: 'inspection_with_media', label: 'Проверка бракованного товара с фотографиями' },
  { value: 'receipt_warehouse', label: 'Приёмка товара на нашем складе' },
  { value: 'receipt_pickup', label: 'Приёмка товара на стороннем ПВЗ' },
  { value: 'balance_top_up', label: 'Пополнение баланса' },
];

const BULK_MODAL_TYPE_OPTIONS = BILLING_TYPE_OPTIONS.filter((o) => o.value);
const BULK_TYPE_VALUES_ALL = BULK_MODAL_TYPE_OPTIONS.map((o) => o.value);

const STORAGE_MERGE_TYPES = new Set(['storage_daily', 'storage_warehouse']);

function logicalChargeType(type) {
  if (!type) return type;
  if (STORAGE_MERGE_TYPES.has(type)) return 'storage_at_warehouse';
  return type;
}

function typeLabelRu(code) {
  const o = BILLING_TYPE_OPTIONS.find((x) => x.value === code);
  if (o) return o.label;
  if (code === 'storage_at_warehouse') return 'Хранение на складе (Flydarin + МП)';
  return code || '—';
}

function aggregateLogicalExpenses(expenses) {
  const map = new Map();
  (expenses || []).forEach((ex) => {
    const lk = logicalChargeType(ex.type);
    if (!map.has(lk)) map.set(lk, { total: 0, count: 0 });
    const row = map.get(lk);
    row.total += parseFloat(ex.amount, 10) || 0;
    row.count += 1;
  });
  return Array.from(map.entries())
    .map(([key, v]) => ({ key, ...v }))
    .sort((a, b) => a.key.localeCompare(b.key));
}

function sumExpensesAmount(expenses) {
  return (expenses || []).reduce((s, ex) => s + (parseFloat(ex.amount, 10) || 0), 0);
}

const ORDER_STATUS_RU = {
  awaiting_receipt: 'Ожидает приёмки',
  received_awaiting_check: 'Принят, ожидает проверки',
  awaiting_seller_decision: 'Ожидает решения продавца',
  awaiting_for_sale: 'Ожидает выставления',
  transferring_to_sale: 'Передаётся в продажу',
  submitted_for_sale: 'Выставлен на продажу',
  awaiting_shipment: 'Ожидает отгрузки',
  awaiting_disposal: 'Ожидает утилизации',
  disposed: 'Утилизирован',
  sent_to_seller: 'Отправлен продавцу',
};

function orderStatusRu(code, displayFallback) {
  if (code && ORDER_STATUS_RU[code]) return ORDER_STATUS_RU[code];
  return displayFallback || code || '—';
}

function OrderDetailPanel({ detail }) {
  const [linesOpen, setLinesOpen] = useState(false);
  const ord = detail;
  if (!ord || !ord.expenses) return null;
  const agg = aggregateLogicalExpenses(ord.expenses);
  const returnTotal = sumExpensesAmount(ord.expenses);
  return (
    <Card body className="shadow-sm border">
      <div className="fw-bold mb-2 text-body">
        {ord.platform_order_no || ord.order_id}
        {' '}
        <Badge bg="info">{orderStatusRu(ord.current_status, ord.status_display)}</Badge>
      </div>
      <div className="small text-muted mb-1">История статусов</div>
      <ul className="small mb-3 ps-3">
        {(ord.status_history || []).map((h, i) => (
          <li key={`${ord.order_id}-${i}`}>
            <span className="text-muted">{h.changed_at?.slice(0, 16) || ''}</span>
            {' · '}
            <span className="text-body">
              {orderStatusRu(h.from_status, h.from_display)}
              {' → '}
              {orderStatusRu(h.to_status, h.to_display)}
            </span>
            {h.changed_by_email ? (
              <span className="text-muted small ms-1">
                (
                {h.changed_by_email}
                )
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      <div className="small fw-semibold mb-2 text-body">
        Сумма списаний по заказу:
        {' '}
        <span className="text-danger">{returnTotal}</span>
        {' ¥'}
      </div>
      <Table size="sm" bordered className="mb-2">
        <thead>
          <tr>
            <th>Тип</th>
            <th>Сумма ¥</th>
          </tr>
        </thead>
        <tbody>
          {agg.map((a) => (
            <tr key={a.key}>
              <td>{typeLabelRu(a.key)}</td>
              <td className="text-danger">{a.total}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Button size="sm" variant="outline-secondary" className="mb-2" onClick={() => setLinesOpen((v) => !v)}>
        {linesOpen ? 'Скрыть строки' : 'Все строки списаний'}
      </Button>
      <Collapse in={linesOpen}>
        <div>
          <Table size="sm" bordered className="mb-0">
            <thead>
              <tr>
                <th>Тип</th>
                <th>Дата начисл.</th>
                <th>Сумма ¥</th>
              </tr>
            </thead>
            <tbody>
              {(ord.expenses || []).map((ex) => {
                const logical = logicalChargeType(ex.type);
                return (
                  <tr key={ex.id}>
                    <td>{typeLabelRu(logical)}</td>
                    <td>{ex.billing_date || '—'}</td>
                    <td className="text-danger">{ex.amount}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </Collapse>
    </Card>
  );
}

const AdminBillingsPage = () => {
  const { t } = useLanguage();
  const { isBillingExpenseRowHighlighted } = useHighlightedStores();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expenses, setExpenses] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedStores, setSelectedStores] = useState([]);
  const [storePickerKey, setStorePickerKey] = useState(0);
  const [orderNoFilter, setOrderNoFilter] = useState('');
  const [exporting, setExporting] = useState(false);
  const [editModal, setEditModal] = useState({ show: false, expense: null });
  const [editAmount, setEditAmount] = useState('');
  const [editReason, setEditReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [topUpModal, setTopUpModal] = useState(false);
  const [topUpUserQuery, setTopUpUserQuery] = useState('');
  const [topUpUserResults, setTopUpUserResults] = useState([]);
  const [topUpSelectedUser, setTopUpSelectedUser] = useState(null);
  const [topUpUserStores, setTopUpUserStores] = useState([]);
  const [topUpStoresLoading, setTopUpStoresLoading] = useState(false);
  const [topUpAmountsByStore, setTopUpAmountsByStore] = useState({});
  const [topUpSubmitting, setTopUpSubmitting] = useState(false);
  const [topUpError, setTopUpError] = useState('');
  const [topUpComment, setTopUpComment] = useState('');

  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState('');
  const [analyticsDateFrom, setAnalyticsDateFrom] = useState('');
  const [analyticsDateTo, setAnalyticsDateTo] = useState('');
  const [analyticsSearch, setAnalyticsSearch] = useState('');
  const [analyticsTypeFilter, setAnalyticsTypeFilter] = useState('');
  const [analyticsOrderNoFilter, setAnalyticsOrderNoFilter] = useState('');
  const [analyticsSelectedStores, setAnalyticsSelectedStores] = useState([]);
  const [analyticsStorePickerKey, setAnalyticsStorePickerKey] = useState(0);
  const [openStorePanels, setOpenStorePanels] = useState({});
  const [orderDetailByKey, setOrderDetailByKey] = useState({});
  const [orderDetailLoading, setOrderDetailLoading] = useState(null);

  const [bulkModal, setBulkModal] = useState(false);
  const [bulkStep, setBulkStep] = useState('form');
  const [bulkModalStores, setBulkModalStores] = useState([]);
  const [bulkStorePickerKey, setBulkStorePickerKey] = useState(0);
  const [bulkSelectedTypes, setBulkSelectedTypes] = useState([]);
  const [bulkOrderLines, setBulkOrderLines] = useState('');
  const [bulkDateFrom, setBulkDateFrom] = useState('');
  const [bulkDateTo, setBulkDateTo] = useState('');
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkPreviewLoading, setBulkPreviewLoading] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const [bulkOnlyDeductions, setBulkOnlyDeductions] = useState(true);

  const [staffFrom, setStaffFrom] = useState('');
  const [staffTo, setStaffTo] = useState('');
  const [staffData, setStaffData] = useState(null);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffError, setStaffError] = useState('');
  const [staffExpanded, setStaffExpanded] = useState({});

  const listFilterParams = useMemo(() => {
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (search) params.search = search;
    if (typeFilter) params.type = typeFilter;
    if (selectedStores.length) params.store_ids = selectedStores.map((s) => s.id);
    if (orderNoFilter) params.order_no = orderNoFilter;
    return params;
  }, [dateFrom, dateTo, search, typeFilter, selectedStores, orderNoFilter]);

  const analyticsFilterParams = useMemo(() => {
    const params = {};
    if (analyticsDateFrom) params.date_from = analyticsDateFrom;
    if (analyticsDateTo) params.date_to = analyticsDateTo;
    if (analyticsSearch) params.search = analyticsSearch;
    if (analyticsTypeFilter) params.type = analyticsTypeFilter;
    if (analyticsSelectedStores.length) {
      params.store_ids = analyticsSelectedStores.map((s) => s.id);
    }
    if (analyticsOrderNoFilter) params.order_no = analyticsOrderNoFilter;
    return params;
  }, [
    analyticsDateFrom,
    analyticsDateTo,
    analyticsSearch,
    analyticsTypeFilter,
    analyticsOrderNoFilter,
    analyticsSelectedStores,
  ]);

  useEffect(() => {
    const t = setTimeout(() => {
      setAnalyticsData(null);
      setOpenStorePanels({});
      setOrderDetailByKey({});
    }, 450);
    return () => clearTimeout(t);
  }, [analyticsFilterParams]);

  const loadExpenses = useCallback(async (pageOverride) => {
    setLoading(true);
    setError('');
    const p = pageOverride !== undefined ? pageOverride : page;
    try {
      const params = { page: p, page_size: pageSize, ...listFilterParams };
      const response = await api.getBillingExpenses(params);
      setExpenses(Array.isArray(response?.data) ? response.data : response?.data?.data ?? []);
      setTotalCount(response?.count ?? 0);
      if (pageOverride !== undefined) setPage(pageOverride);
    } catch (err) {
      setError(err.message || 'Failed to load billings');
      setExpenses([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [listFilterParams, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [dateFrom, dateTo, search, typeFilter, orderNoFilter, selectedStores]);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  useEffect(() => {
    if (!topUpModal || !topUpSelectedUser) {
      setTopUpUserStores([]);
      setTopUpAmountsByStore({});
      return;
    }
    setTopUpStoresLoading(true);
    api.getBillingUserStores(topUpSelectedUser.id)
      .then((r) => {
        const list = Array.isArray(r?.data) ? r.data : (Array.isArray(r?.data?.data) ? r.data.data : []);
        setTopUpUserStores(list);
        setTopUpAmountsByStore(list.reduce((acc, s) => ({ ...acc, [String(s.id)]: '' }), {}));
      })
      .catch(() => {
        setTopUpUserStores([]);
        setTopUpAmountsByStore({});
      })
      .finally(() => setTopUpStoresLoading(false));
  }, [topUpModal, topUpSelectedUser?.id]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.downloadBillingExpensesExcel(listFilterParams);
      const blob = res?.data;
      if (!blob) throw new Error('No file received');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `billing_expenses_${dateFrom || 'all'}_${dateTo || 'all'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    setAnalyticsError('');
    try {
      const data = await api.getBillingAdminAnalytics(analyticsFilterParams);
      setAnalyticsData(data);
    } catch (err) {
      setAnalyticsData(null);
      setAnalyticsError(err.message || 'Не удалось загрузить аналитику');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadStaffStatusAnalytics = async () => {
    setStaffLoading(true);
    setStaffError('');
    try {
      const params = {};
      if (staffFrom) params.date_from = staffFrom;
      if (staffTo) params.date_to = staffTo;
      const data = await api.getBillingAdminStaffStatusAnalytics(params);
      setStaffData(data);
      setStaffExpanded({});
    } catch (err) {
      setStaffData(null);
      setStaffError(err.message || t('adminStaffStatus.loadError'));
    } finally {
      setStaffLoading(false);
    }
  };

  const toggleStorePanel = (sid) => {
    const k = String(sid);
    setOpenStorePanels((prev) => ({ ...prev, [k]: !prev[k] }));
  };

  const orderDetailKey = (storeId, type, orderId) => `${storeId}:${type}:${orderId}`;

  const toggleOrderDetail = async (storeId, type, orderId) => {
    const key = orderDetailKey(storeId, type, orderId);
    if (orderDetailByKey[key]) {
      setOrderDetailByKey((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }
    setOrderDetailLoading(key);
    try {
      const params = { ...analyticsFilterParams, order_id: orderId };
      const data = await api.getBillingAdminOrderDetail(params);
      setOrderDetailByKey((prev) => ({ ...prev, [key]: data }));
    } catch (err) {
      setOrderDetailByKey((prev) => ({
        ...prev,
        [key]: { error: err.message || 'Ошибка загрузки' },
      }));
    } finally {
      setOrderDetailLoading(null);
    }
  };

  const removeSelectedStore = (id) => {
    setSelectedStores((prev) => prev.filter((s) => String(s.id) !== String(id)));
  };

  const onStorePicked = (s) => {
    if (!s) return;
    const idStr = String(s.id);
    setSelectedStores((prev) => {
      if (prev.some((x) => String(x.id) === idStr)) return prev;
      return [...prev, { id: s.id, name: s.name || '', store_id: s.store_id }];
    });
    setStorePickerKey((k) => k + 1);
  };

  const removeAnalyticsSelectedStore = (id) => {
    setAnalyticsSelectedStores((prev) => prev.filter((s) => String(s.id) !== String(id)));
  };

  const onAnalyticsStorePicked = (s) => {
    if (!s) return;
    const idStr = String(s.id);
    setAnalyticsSelectedStores((prev) => {
      if (prev.some((x) => String(x.id) === idStr)) return prev;
      return [...prev, { id: s.id, name: s.name || '', store_id: s.store_id }];
    });
  };

  const copyTableFiltersToAnalytics = () => {
    setAnalyticsDateFrom(dateFrom);
    setAnalyticsDateTo(dateTo);
    setAnalyticsSearch(search);
    setAnalyticsTypeFilter(typeFilter);
    setAnalyticsOrderNoFilter(orderNoFilter);
    setAnalyticsSelectedStores(selectedStores.map((s) => ({ ...s })));
    setAnalyticsStorePickerKey((k) => k + 1);
  };

  const openBulkModal = () => {
    setBulkModal(true);
    setBulkStep('form');
    setBulkPreview(null);
    setBulkError('');
    setBulkModalStores([]);
    setBulkStorePickerKey((k) => k + 1);
    setBulkSelectedTypes([]);
    setBulkOrderLines('');
    setBulkDateFrom('');
    setBulkDateTo('');
    setBulkOnlyDeductions(true);
  };

  const removeBulkModalStore = (id) => {
    setBulkModalStores((prev) => prev.filter((s) => String(s.id) !== String(id)));
  };

  const onBulkModalStorePicked = (s) => {
    if (!s) return;
    const idStr = String(s.id);
    setBulkModalStores((prev) => {
      if (prev.some((x) => String(x.id) === idStr)) return prev;
      return [...prev, { id: s.id, name: s.name || '', store_id: s.store_id }];
    });
    setBulkStorePickerKey((k) => k + 1);
  };

  const toggleBulkType = (code) => {
    setBulkSelectedTypes((prev) => (
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    ));
  };

  const toggleAllBulkTypes = () => {
    setBulkSelectedTypes((prev) => (
      prev.length === BULK_TYPE_VALUES_ALL.length ? [] : [...BULK_TYPE_VALUES_ALL]
    ));
  };

  const buildBulkDeletePayload = () => {
    const store_ids = bulkModalStores.map((s) => s.id);
    const orderLines = bulkOrderLines.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const body = {
      store_ids,
      only_deductions: bulkOnlyDeductions,
    };
    if (bulkDateFrom) body.date_from = bulkDateFrom;
    if (bulkDateTo) body.date_to = bulkDateTo;
    if (bulkSelectedTypes.length) body.types = bulkSelectedTypes;
    if (orderLines.length) body.order_platform_nos = orderLines;
    return body;
  };

  const runBulkPreview = async () => {
    if (!bulkModalStores.length) {
      setBulkError('Выберите хотя бы один магазин.');
      return;
    }
    setBulkPreviewLoading(true);
    setBulkError('');
    try {
      const res = await api.billingExpensesBulkDelete({
        ...buildBulkDeletePayload(),
        dry_run: true,
      });
      setBulkPreview(res);
      setBulkStep('preview');
    } catch (err) {
      setBulkPreview(null);
      setBulkError(err.message || 'Ошибка предпросмотра');
    } finally {
      setBulkPreviewLoading(false);
    }
  };

  const runBulkDelete = async () => {
    setBulkDeleting(true);
    setBulkError('');
    try {
      await api.billingExpensesBulkDelete({
        ...buildBulkDeletePayload(),
        dry_run: false,
        confirm: true,
      });
      setBulkModal(false);
      setBulkPreview(null);
      setBulkStep('form');
      loadExpenses(1);
      setAnalyticsData(null);
    } catch (err) {
      setBulkError(err.message || 'Ошибка удаления');
    } finally {
      setBulkDeleting(false);
    }
  };

  const openEdit = (expense) => {
    setEditModal({ show: true, expense });
    setEditAmount(expense.amount != null ? String(expense.amount) : '');
    setEditReason(expense.admin_override_reason || '');
    setSaveError('');
  };

  const closeEdit = () => {
    setEditModal({ show: false, expense: null });
    setSaveError('');
  };

  const handleSave = async () => {
    if (!editModal.expense) return;
    const expense = editModal.expense;
    const autoAmount = expense.auto_amount != null ? Number(expense.auto_amount) : null;
    const newAmount = parseFloat(editAmount);
    if (Number.isNaN(newAmount)) {
      setSaveError('Введите число');
      return;
    }
    const isTopUp = expense.type === 'balance_top_up';
    if (isTopUp && newAmount <= 0) {
      setSaveError('Пополнение: сумма должна быть положительной.');
      return;
    }
    if (!isTopUp && newAmount >= 0) {
      setSaveError('Списание: сумма должна быть отрицательной.');
      return;
    }
    if (autoAmount != null && newAmount !== autoAmount && !editReason.trim()) {
      setSaveError('При изменении суммы от авто-расчёта укажите причину.');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      await api.updateBillingExpense(expense.id, {
        amount: newAmount,
        admin_override_reason: editReason.trim() || undefined,
      });
      loadExpenses();
      closeEdit();
    } catch (err) {
      const data = err.data || {};
      setSaveError(data.admin_override_reason?.[0] || data.amount?.[0] || err.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRow = async (exp) => {
    if (!window.confirm(`Удалить запись биллинга #${exp.id} (${exp.type_display || exp.type})?`)) return;
    try {
      await api.deleteBillingExpense(exp.id);
      loadExpenses();
      setAnalyticsData(null);
    } catch (err) {
      setError(err.message || 'Не удалось удалить');
    }
  };

  const dateDisplay = (exp) => {
    if (exp.billing_date) return exp.billing_date;
    if (exp.created_at) return exp.created_at.slice(0, 10);
    return '—';
  };

  return (
    <Container fluid className="admin-billings-page py-4 px-3" style={{ maxWidth: '100%' }}>
      <Row className="mb-4">
        <Col>
          <div className="rounded-3 border border-2 border-danger p-3 d-flex flex-wrap align-items-center gap-3 bg-body-secondary">
            <Button
              variant="danger"
              size="lg"
              className="fw-bold px-4 shadow-sm text-nowrap"
              onClick={openBulkModal}
            >
              Массовая отмена списаний
            </Button>
            <span className="text-muted small mb-0" style={{ maxWidth: 520 }}>
              Отдельная операция: магазины, типы списаний, список заказов и даты задаются только здесь и не зависят от фильтров таблицы ниже.
            </span>
          </div>
        </Col>
      </Row>

      <Card className="w-100 mb-4" style={{ maxWidth: '100%' }}>
        <Card.Header as="h5">Аналитика биллинга</Card.Header>
        <Card.Body className="p-3">
          <p className="text-muted small mb-2">
            Фильтры ниже действуют только на этот блок. Таблица списаний использует отдельные фильтры.
          </p>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Button variant="outline-secondary" size="sm" onClick={copyTableFiltersToAnalytics}>
              Скопировать из фильтров таблицы
            </Button>
          </div>
          <Row className="mb-2 align-items-end g-2">
            <Col xs={6} md={2}>
              <Form.Group>
                <Form.Label className="small mb-0">Дата с</Form.Label>
                <Form.Control
                  type="date"
                  value={analyticsDateFrom}
                  onChange={(e) => setAnalyticsDateFrom(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={6} md={2}>
              <Form.Group>
                <Form.Label className="small mb-0">Дата по</Form.Label>
                <Form.Control
                  type="date"
                  value={analyticsDateTo}
                  onChange={(e) => setAnalyticsDateTo(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={2}>
              <Form.Group>
                <Form.Label className="small mb-0">Тип</Form.Label>
                <Form.Select
                  value={analyticsTypeFilter}
                  onChange={(e) => setAnalyticsTypeFilter(e.target.value)}
                >
                  {BILLING_TYPE_OPTIONS.map((o) => (
                    <option key={o.value || 'all'} value={o.value}>{o.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={2}>
              <Form.Group>
                <Form.Label className="small mb-0">Номер заказа</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="mp.xxx…"
                  value={analyticsOrderNoFilter}
                  onChange={(e) => setAnalyticsOrderNoFilter(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6} lg={4}>
              <Form.Group>
                <Form.Label className="small mb-0">Магазины</Form.Label>
                <AdminStoreSearchCombobox
                  key={analyticsStorePickerKey}
                  filterValue=""
                  onFilterValueChange={() => {}}
                  onStoreSelected={onAnalyticsStorePicked}
                  valueField="id"
                  placeholder="Добавить магазин…"
                  noMatchesText="Нет совпадений"
                  loadingText="Поиск…"
                  clearInputAfterPick
                />
                <div className="d-flex flex-wrap gap-1 mt-1">
                  {analyticsSelectedStores.map((s) => (
                    <Badge
                      key={`a-${s.id}`}
                      bg="primary"
                      className="d-flex align-items-center gap-1"
                      style={{ cursor: 'pointer' }}
                      onClick={() => removeAnalyticsSelectedStore(s.id)}
                      title="Нажмите, чтобы убрать"
                    >
                      {s.name || s.store_id}
                      {' '}
                      ×
                    </Badge>
                  ))}
                  {analyticsSelectedStores.length === 0 && (
                    <span className="text-muted small">Все магазины</span>
                  )}
                </div>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-2 align-items-end g-2">
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="small mb-0">Поиск (товар, barcode)</Form.Label>
                <Form.Control
                  type="text"
                  value={analyticsSearch}
                  onChange={(e) => setAnalyticsSearch(e.target.value)}
                  placeholder="Название товара, barcode…"
                />
              </Form.Group>
            </Col>
          </Row>
          {analyticsError && <Alert variant="danger">{analyticsError}</Alert>}
          <Button variant="primary" className="mb-3" onClick={loadAnalytics} disabled={analyticsLoading}>
            {analyticsLoading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
            Рассчитать аналитику
          </Button>
          {analyticsData && (
            <>
              <h6 className="text-muted mt-2">1.1 Общая сводка</h6>
              <Table size="sm" bordered className="mb-4" style={{ maxWidth: 520 }}>
                <tbody>
                  <tr>
                    <td>Начисления на баланс (пополнения)</td>
                    <td className="text-end text-success">{analyticsData.overall?.top_up_total} ¥</td>
                  </tr>
                  <tr>
                    <td>Списания (сумма отрицательных строк)</td>
                    <td className="text-end text-danger">{analyticsData.overall?.deductions_total} ¥</td>
                  </tr>
                  <tr>
                    <td>Списания по модулю</td>
                    <td className="text-end">{analyticsData.overall?.deductions_abs} ¥</td>
                  </tr>
                  <tr>
                    <td>Сальдо по отфильтрованным строкам</td>
                    <td className="text-end fw-bold">{analyticsData.overall?.net_amount} ¥</td>
                  </tr>
                  <tr>
                    <td>Число записей</td>
                    <td className="text-end">{analyticsData.overall?.record_count}</td>
                  </tr>
                </tbody>
              </Table>

              <h6 className="text-muted">1.2 По типам строк биллинга</h6>
              <p className="small text-muted mb-2">
                Сумма и число строк в отфильтрованной выборке по каждому типу услуги (пополнения — положительная сумма, списания — отрицательная).
              </p>
              <div className="table-responsive mb-4">
                <Table size="sm" striped bordered hover>
                  <thead>
                    <tr>
                      <th>Тип</th>
                      <th className="text-end">Сумма ¥</th>
                      <th className="text-end">Строк</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analyticsData.by_service_type || []).length === 0 ? (
                      <tr>
                        <td colSpan={3} className="text-muted">Нет данных</td>
                      </tr>
                    ) : (
                      (analyticsData.by_service_type || []).map((row) => {
                        const n = parseFloat(String(row.total).replace(',', '.'), 10);
                        const sumCls = Number.isNaN(n) ? '' : (n > 0 ? 'text-success' : n < 0 ? 'text-danger' : '');
                        return (
                          <tr key={row.type}>
                            <td>{row.type_display || row.type}</td>
                            <td className={`text-end fw-semibold ${sumCls}`}>{row.total}</td>
                            <td className="text-end">{row.row_count}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>

              <h6 className="text-muted">1.3 Заказы в выборке по статусам за период фильтра (история)</h6>
              <p className="small text-muted mb-2">
                Только история статусов (OrderStatusHistory), без учёта строк биллинга.
                Учитываются заказы выбранных магазинов (тот же фильтр магазина, что у таблицы расходов).
                По каждому статусу — сколько заказов вошли в этот статус в периоде «дата с / дата по» калькулятора
                (как «Переход в статус (история)» в админке заказов).
                Всего заказов в этой выборке по магазину:
                {' '}
                <strong>{analyticsData.distinct_orders_total ?? 0}</strong>
                .
              </p>
              <div className="table-responsive mb-4">
                <Table size="sm" striped bordered hover>
                  <thead>
                    <tr>
                      <th>Статус заказа</th>
                      <th className="text-end">Заказов</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analyticsData.orders_by_status || []).length === 0 ? (
                      <tr>
                        <td colSpan={2} className="text-muted">Нет заказов по выбранным магазинам</td>
                      </tr>
                    ) : (
                      (analyticsData.orders_by_status || []).map((row) => (
                        <tr key={row.status || 'unknown'}>
                          <td>{row.status_display || row.status || '—'}</td>
                          <td className="text-end">{row.order_count}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              <h6 className="text-muted">1.4 По магазинам</h6>
              <div className="table-responsive mb-4">
                <Table size="sm" striped bordered hover>
                  <thead>
                    <tr>
                      <th>Магазин</th>
                      <th>ID (WB)</th>
                      <th className="text-end">Пополнения ¥</th>
                      <th className="text-end">Списания ¥</th>
                      <th className="text-end">Сальдо ¥</th>
                      <th className="text-end">Записей</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analyticsData.by_store || []).map((row) => (
                      <tr key={`${String(row.store_external_id || '')}:${String(row.store_id || '')}:${String(row.store_name || '')}`}>
                        <td>{row.store_name || '—'}</td>
                        <td className="font-monospace small">{row.store_external_id || row.store_id || '—'}</td>
                        <td className="text-end text-success">{row.top_up_total}</td>
                        <td className="text-end text-danger">{row.deductions_total}</td>
                        <td className="text-end">{row.net_amount}</td>
                        <td className="text-end">{row.record_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <h6 className="text-muted">1.5 По магазинам и типам списаний / заказам</h6>
              {analyticsData.order_groups_truncated && (
                <Alert variant="warning" className="py-2 small">
                  Показаны первые {analyticsData.order_groups_limit} групп заказ×тип; уменьшите фильтр, чтобы увидеть всё.
                </Alert>
              )}
              {(analyticsData.by_store_by_type || []).map((block) => {
                const sk = `${String(block.store_external_id || '')}:${String(block.store_id || '')}:${String(block.store_name || '')}`;
                const open = !!openStorePanels[sk];
                return (
                  <Card key={sk} className="mb-2">
                    <Card.Header
                      className="py-2 d-flex justify-content-between align-items-center"
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleStorePanel(sk)}
                    >
                      <span>
                        <strong>{block.store_name || 'Без названия'}</strong>
                        {' '}
                        <span className="text-muted small font-monospace">{block.store_external_id}</span>
                      </span>
                      <span className="small text-muted">{open ? '▼' : '▶'}</span>
                    </Card.Header>
                    <Collapse in={open}>
                      <Card.Body className="pt-2 pb-3">
                        {(block.types || []).map((trow) => (
                          <div key={trow.type} className="mb-3">
                            <div className="fw-semibold small mb-1">
                              {trow.type_display || trow.type}
                              {' '}
                              <span className="text-muted">
                                (
                                {trow.orders?.length || 0}
                                {' '}
                                заказ.)
                              </span>
                            </div>
                            <Table size="sm" bordered responsive className="mb-0">
                              <thead>
                                <tr>
                                  <th>Заказ</th>
                                  <th className="text-end">Сумма ¥</th>
                                  <th className="text-end">Строк</th>
                                  <th style={{ width: 120 }} />
                                </tr>
                              </thead>
                              <tbody>
                                {(trow.orders || []).map((o) => {
                                  const ok = orderDetailKey(sk, trow.type, o.order_id);
                                  const loading = orderDetailLoading === ok;
                                  const detail = orderDetailByKey[ok];
                                  return (
                                    <React.Fragment key={o.order_id}>
                                      <tr>
                                        <td className="font-monospace small">{o.platform_order_no || o.order_id}</td>
                                        <td className="text-end">{o.order_total}</td>
                                        <td className="text-end">{o.line_count}</td>
                                        <td>
                                          <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            disabled={loading}
                                            onClick={() => toggleOrderDetail(sk, trow.type, o.order_id)}
                                          >
                                            {loading ? <Spinner animation="border" size="sm" /> : (detail ? 'Скрыть' : 'Детали')}
                                          </Button>
                                        </td>
                                      </tr>
                                      {detail && (
                                        <tr>
                                          <td colSpan={4} className="bg-light p-3">
                                            {detail.error && <Alert variant="warning">{detail.error}</Alert>}
                                            {!detail.error && <OrderDetailPanel detail={detail} />}
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </Table>
                          </div>
                        ))}
                      </Card.Body>
                    </Collapse>
                  </Card>
                );
              })}

              <h6 className="text-muted mt-4">3. Новые магазины (WB) по датам фильтра</h6>
              <p className="small text-muted mb-2">
                Записи Store с датой создания в интервале «дата с / дата по» калькулятора; только Wildberries, не удалённые.
                Фильтр магазинов (store_ids) и поиск (название, WB store_id, email пользователя) — как у блока выше; тип строки биллинга и номер заказа на этот блок не влияют.
                Успех по ключу — верификация WB; полный успех — верификация и заполнены client/supplier/shop/warehouse + binding в WH.
              </p>
              {analyticsData.new_stores?.empty_store_filter && (
                <Alert variant="secondary" className="py-2 small">
                  Выбран пустой список магазинов — укажите магазины или снимите фильтр.
                </Alert>
              )}
              {!analyticsData.new_stores?.empty_store_filter && (
                <div className="table-responsive mb-4">
                  <Table size="sm" bordered style={{ maxWidth: 640 }}>
                    <tbody>
                      <tr>
                        <td>Всего новых магазинов в периоде</td>
                        <td className="text-end fw-semibold">{analyticsData.new_stores?.total ?? 0}</td>
                      </tr>
                      <tr>
                        <td>Успешно по ключу WB (верифицированы)</td>
                        <td className="text-end text-success">{analyticsData.new_stores?.verified_key_count ?? 0}</td>
                      </tr>
                      <tr>
                        <td>Успешно по ключу и WH (полная интеграция)</td>
                        <td className="text-end text-success">{analyticsData.new_stores?.fully_connected_count ?? 0}</td>
                      </tr>
                      <tr>
                        <td>Неуспешно / не полностью (всего минус полный WH)</td>
                        <td className="text-end text-danger">{analyticsData.new_stores?.unsuccessful_total ?? 0}</td>
                      </tr>
                      <tr className="table-light">
                        <td colSpan={2} className="small text-muted py-1">Детализация «не полный успех»</td>
                      </tr>
                      <tr>
                        <td className="ps-3">Ожидают верификацию ключа</td>
                        <td className="text-end">{analyticsData.new_stores?.pending_count ?? 0}</td>
                      </tr>
                      <tr>
                        <td className="ps-3">Верификация ключа не прошла</td>
                        <td className="text-end">{analyticsData.new_stores?.failed_count ?? 0}</td>
                      </tr>
                      <tr>
                        <td className="ps-3">Ключ ок, но WH не завершён</td>
                        <td className="text-end">{analyticsData.new_stores?.verified_wh_incomplete_count ?? 0}</td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      <Card className="w-100 mb-4" style={{ maxWidth: '100%' }}>
        <Card.Header as="h5">{t('adminStaffStatus.title')}</Card.Header>
        <Card.Body className="p-3">
          <p className="text-muted small mb-3">{t('adminStaffStatus.intro')}</p>
          <Row className="mb-3 align-items-end g-2">
            <Col xs={6} md={2}>
              <Form.Group>
                <Form.Label className="small mb-0">{t('adminStaffStatus.dateFrom')}</Form.Label>
                <Form.Control type="date" value={staffFrom} onChange={(e) => setStaffFrom(e.target.value)} />
              </Form.Group>
            </Col>
            <Col xs={6} md={2}>
              <Form.Group>
                <Form.Label className="small mb-0">{t('adminStaffStatus.dateTo')}</Form.Label>
                <Form.Control type="date" value={staffTo} onChange={(e) => setStaffTo(e.target.value)} />
              </Form.Group>
            </Col>
            <Col xs={12} md="auto">
              <Button variant="primary" onClick={loadStaffStatusAnalytics} disabled={staffLoading}>
                {staffLoading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                {t('adminStaffStatus.load')}
              </Button>
            </Col>
          </Row>
          {staffError ? <Alert variant="danger">{staffError}</Alert> : null}
          {staffData && Array.isArray(staffData.staff) && (
            <div className="table-responsive">
              <Table bordered size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>{t('adminStaffStatus.colEmail')}</th>
                    <th className="text-end">{t('adminStaffStatus.colTotal')}</th>
                    <th>{t('adminStaffStatus.colTransitions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {staffData.staff.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-muted">
                        {t('adminStaffStatus.empty')}
                      </td>
                    </tr>
                  ) : (
                    staffData.staff.map((row) => {
                      const uid = String(row.user_id);
                      const open = !!staffExpanded[uid];
                      return (
                        <React.Fragment key={uid}>
                          <tr>
                            <td className="align-middle">
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 me-1 text-decoration-none"
                                onClick={() => setStaffExpanded((prev) => ({ ...prev, [uid]: !prev[uid] }))}
                              >
                                <i className={`bi bi-chevron-${open ? 'down' : 'right'}`} />
                              </Button>
                              {row.email || `id:${row.user_id}`}
                            </td>
                            <td className="text-end align-middle">{row.total_transitions}</td>
                            <td className="small text-muted align-middle">
                              {(row.by_transition || []).map((tr) => (
                                <span key={`${tr.from_status}-${tr.to_status}`} className="me-2 d-inline-block">
                                  {tr.from_display} → {tr.to_display}: <strong>{tr.count}</strong>
                                </span>
                              ))}
                            </td>
                          </tr>
                          {open && (
                            <tr>
                              <td colSpan={3} className="bg-light p-0">
                                <Table size="sm" className="mb-0">
                                  <thead>
                                    <tr>
                                      <th>{t('adminStaffStatus.from')}</th>
                                      <th>{t('adminStaffStatus.to')}</th>
                                      <th className="text-end">{t('adminStaffStatus.count')}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(row.by_transition || []).map((tr) => (
                                      <tr key={`${tr.from_status}-${tr.to_status}`}>
                                        <td>{tr.from_display}</td>
                                        <td>{tr.to_display}</td>
                                        <td className="text-end">{tr.count}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Card className="w-100" style={{ maxWidth: '100%' }}>
        <Card.Header as="h5">Billings</Card.Header>
        <Card.Body className="p-3" style={{ overflow: 'visible' }}>
          {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
          <Row className="mb-2 align-items-end">
            <Col md={2}>
              <Form.Group>
                <Form.Label className="small">Дата с</Form.Label>
                <Form.Control type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="small">Дата по</Form.Label>
                <Form.Control type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="small">Тип</Form.Label>
                <Form.Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                  {BILLING_TYPE_OPTIONS.map((o) => (
                    <option key={o.value || 'all'} value={o.value}>{o.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="small">Номер заказа</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="mp.xxx или часть номера"
                  value={orderNoFilter}
                  onChange={(e) => setOrderNoFilter(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="small">Магазины (несколько)</Form.Label>
                <AdminStoreSearchCombobox
                  key={storePickerKey}
                  filterValue=""
                  onFilterValueChange={() => {}}
                  onStoreSelected={onStorePicked}
                  valueField="id"
                  placeholder="Добавить магазин по названию, ID…"
                  noMatchesText="Нет совпадений"
                  loadingText="Поиск…"
                />
                <div className="d-flex flex-wrap gap-1 mt-2">
                  {selectedStores.map((s) => (
                    <Badge
                      key={s.id}
                      bg="secondary"
                      className="d-flex align-items-center gap-1"
                      style={{ cursor: 'pointer' }}
                      onClick={() => removeSelectedStore(s.id)}
                      title="Нажмите, чтобы убрать"
                    >
                      {s.name || s.store_id}
                      {' '}
                      ×
                    </Badge>
                  ))}
                  {selectedStores.length === 0 && (
                    <span className="text-muted small">Все магазины</span>
                  )}
                </div>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="small">Поиск (товар, barcode)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Название товара, barcode..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3 align-items-center">
            <Col className="d-flex flex-wrap gap-2">
              <Button variant="outline-primary" onClick={() => loadExpenses(1)}>Применить фильтры</Button>
              <Button variant="outline-success" onClick={handleExport} disabled={exporting}>
                {exporting ? <Spinner animation="border" size="sm" /> : 'Скачать Excel'}
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setTopUpModal(true);
                  setTopUpError('');
                  setTopUpSelectedUser(null);
                  setTopUpAmountsByStore({});
                  setTopUpUserQuery('');
                  setTopUpUserResults([]);
                  setTopUpComment('');
                }}
              >
                Пополнить аккаунт
              </Button>
            </Col>
          </Row>
          {loading ? (
            <div className="d-flex justify-content-center py-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              {expenses.length === 0 && !error && (
                <p className="text-muted mb-0">No billing expenses yet.</p>
              )}
              {totalCount > 0 && (
                <Row className="mb-2 align-items-center">
                  <Col>
                    <span className="text-muted">
                      Показано {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} из {totalCount}
                    </span>
                  </Col>
                  <Col md="auto" className="d-flex align-items-center gap-2">
                    <Form.Select
                      size="sm"
                      style={{ width: 'auto' }}
                      value={pageSize}
                      onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                    >
                      {PAGE_SIZE_OPTIONS.map((n) => (
                        <option key={n} value={n}>{n} на странице</option>
                      ))}
                    </Form.Select>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Назад
                    </Button>
                    <span className="small">Стр. {page} из {Math.max(1, Math.ceil(totalCount / pageSize))}</span>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={page >= Math.ceil(totalCount / pageSize)}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Вперёд
                    </Button>
                  </Col>
                </Row>
              )}
              {expenses.length > 0 && (
                <div className="table-responsive" style={{ width: '100%', overflowX: 'auto' }}>
                  <Table responsive striped bordered hover size="sm" className="mb-0" style={{ minWidth: 1200, width: '100%', tableLayout: 'auto' }}>
                    <thead>
                      <tr>
                        <th style={{ minWidth: 90 }}>Дата начисл.</th>
                        <th style={{ minWidth: 120 }}>Услуга</th>
                        <th style={{ minWidth: 140 }}>Номер заказа</th>
                        <th style={{ minWidth: 100 }}>Статус заказа</th>
                        <th style={{ minWidth: 90 }}>Дата заказа</th>
                        <th style={{ minWidth: 180 }}>Товар</th>
                        <th style={{ minWidth: 100 }}>barcode</th>
                        <th style={{ minWidth: 90 }}>Кол-во WH</th>
                        <th style={{ minWidth: 80 }}>Сумма (¥)</th>
                        <th style={{ minWidth: 80 }}>Auto (¥)</th>
                        <th style={{ minWidth: 150 }}>Причина изменения</th>
                        <th style={{ minWidth: 140 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {expenses.map((exp) => (
                        <tr
                          key={exp.id}
                          className={
                            isBillingExpenseRowHighlighted(exp) ? 'admin-highlight-store-order' : undefined
                          }
                        >
                          <td>{dateDisplay(exp)}</td>
                          <td>{exp.type_display || exp.type}</td>
                          <td>{exp.order_platform_order_no || exp.order || '—'}</td>
                          <td>{exp.order_status_display || exp.order_status || '—'}</td>
                          <td>{exp.order_created_at ? exp.order_created_at.slice(0, 10) : '—'}</td>
                          <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }} title={exp.product}>{exp.product != null && exp.product !== '' ? exp.product : '—'}</td>
                          <td>{exp.barcode != null && exp.barcode !== '' ? exp.barcode : '—'}</td>
                          <td>{exp.wh_stock_units != null ? exp.wh_stock_units : '—'}</td>
                          <td>{exp.amount}</td>
                          <td>{exp.auto_amount != null ? exp.auto_amount : '—'}</td>
                          <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }} title={exp.admin_override_reason}>{exp.admin_override_reason || '—'}</td>
                          <td className="text-nowrap">
                            <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openEdit(exp)}>Edit</Button>
                            <Button size="sm" variant="outline-danger" onClick={() => handleDeleteRow(exp)}>Удалить</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={bulkModal}
        onHide={() => {
          if (!bulkDeleting && !bulkPreviewLoading) setBulkModal(false);
        }}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>Массовая отмена списаний</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {bulkError && <Alert variant="danger">{bulkError}</Alert>}
          {bulkStep === 'form' && (
            <>
              <p className="small text-muted mb-3">
                Укажите магазины (обязательно). Типы: если ничего не отмечено — учитываются все типы строк.
                Номера заказов — по одному в строке; если поле пустое — по всем заказам, подходящим под остальные условия.
                Даты биллинга пустые — за всё время.
              </p>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Магазины</Form.Label>
                <AdminStoreSearchCombobox
                  key={bulkStorePickerKey}
                  filterValue=""
                  onFilterValueChange={() => {}}
                  onStoreSelected={onBulkModalStorePicked}
                  valueField="id"
                  placeholder="Добавить магазин…"
                  noMatchesText="Нет совпадений"
                  loadingText="Поиск…"
                />
                <div className="d-flex flex-wrap gap-1 mt-2">
                  {bulkModalStores.map((s) => (
                    <Badge
                      key={s.id}
                      bg="secondary"
                      className="d-flex align-items-center gap-1"
                      style={{ cursor: 'pointer' }}
                      onClick={() => removeBulkModalStore(s.id)}
                      title="Нажмите, чтобы убрать"
                    >
                      {s.name || s.store_id}
                      {' '}
                      ×
                    </Badge>
                  ))}
                  {bulkModalStores.length === 0 && (
                    <span className="text-muted small">Ни одного магазина не выбрано</span>
                  )}
                </div>
              </Form.Group>
              <Form.Group className="mb-3">
                <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                  <Form.Label className="fw-semibold mb-0">Типы списаний</Form.Label>
                  <Button size="sm" variant="outline-secondary" type="button" onClick={toggleAllBulkTypes}>
                    {bulkSelectedTypes.length === BULK_TYPE_VALUES_ALL.length ? 'Снять все' : 'Выбрать все'}
                  </Button>
                  <span className="text-muted small">Пустой выбор = все типы</span>
                </div>
                <Row className="g-2">
                  {BULK_MODAL_TYPE_OPTIONS.map((o) => (
                    <Col xs={6} md={4} key={o.value}>
                      <Form.Check
                        type="checkbox"
                        id={`bulk-type-${o.value}`}
                        label={o.label}
                        checked={bulkSelectedTypes.includes(o.value)}
                        onChange={() => toggleBulkType(o.value)}
                      />
                    </Col>
                  ))}
                </Row>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Номера заказов (опционально)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder="По одному номеру в строке. Пусто — все заказы по выбранным магазинам и фильтрам."
                  value={bulkOrderLines}
                  onChange={(e) => setBulkOrderLines(e.target.value)}
                />
              </Form.Group>
              <Row className="mb-3 g-2">
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Дата биллинга с</Form.Label>
                    <Form.Control
                      type="date"
                      value={bulkDateFrom}
                      onChange={(e) => setBulkDateFrom(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Дата биллинга по</Form.Label>
                    <Form.Control
                      type="date"
                      value={bulkDateTo}
                      onChange={(e) => setBulkDateTo(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Check
                type="checkbox"
                id="bulk-only-ded"
                label="Только списания (отрицательные суммы, без пополнений баланса)"
                checked={bulkOnlyDeductions}
                onChange={(e) => setBulkOnlyDeductions(e.target.checked)}
                className="mb-0"
              />
              {!bulkOnlyDeductions && (
                <Alert variant="warning" className="mt-2 py-2 mb-0 small">
                  Снята защита «только списания»: в выборку могут попасть пополнения и прочие положительные строки.
                </Alert>
              )}
            </>
          )}
          {bulkStep === 'preview' && bulkPreview && (
            <>
              <Alert variant="info" className="py-2">
                Будет удалено записей:
                {' '}
                <strong>{bulkPreview.matched_count}</strong>
              </Alert>
              {(bulkPreview.by_type && bulkPreview.by_type.length > 0) ? (
                <div className="table-responsive mb-3">
                  <Table size="sm" bordered className="mb-0">
                    <thead>
                      <tr>
                        <th>Тип</th>
                        <th className="text-end">Количество строк</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkPreview.by_type.map((row) => (
                        <tr key={row.type}>
                          <td>{typeLabelRu(row.type)}</td>
                          <td className="text-end">{row.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <p className="text-muted small">Нет строк, подходящих под условия.</p>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex flex-wrap gap-2">
          <Button
            variant="secondary"
            onClick={() => setBulkModal(false)}
            disabled={bulkDeleting || bulkPreviewLoading}
          >
            Закрыть
          </Button>
          {bulkStep === 'form' && (
            <Button variant="primary" onClick={runBulkPreview} disabled={bulkPreviewLoading || bulkDeleting}>
              {bulkPreviewLoading ? <Spinner animation="border" size="sm" /> : 'Предпросмотр'}
            </Button>
          )}
          {bulkStep === 'preview' && (
            <>
              <Button
                variant="outline-secondary"
                onClick={() => { setBulkStep('form'); setBulkPreview(null); }}
                disabled={bulkDeleting || bulkPreviewLoading}
              >
                Назад к параметрам
              </Button>
              <Button variant="outline-primary" onClick={runBulkPreview} disabled={bulkPreviewLoading || bulkDeleting}>
                {bulkPreviewLoading ? <Spinner animation="border" size="sm" /> : 'Обновить предпросмотр'}
              </Button>
              <Button
                variant="danger"
                onClick={runBulkDelete}
                disabled={bulkDeleting || bulkPreviewLoading || !bulkPreview || bulkPreview.matched_count === 0}
              >
                {bulkDeleting ? <Spinner animation="border" size="sm" /> : 'Подтвердить удаление'}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      <Modal show={editModal.show} onHide={closeEdit}>
        <Modal.Header closeButton>
          <Modal.Title>Редактировать списание / пополнение</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {saveError && <Alert variant="danger">{saveError}</Alert>}
          {editModal.expense && (
            <>
              {editModal.expense.auto_amount != null && (
                <p className="text-muted small mb-2">
                  Авто-сумма: {editModal.expense.auto_amount} ¥. Если изменить сумму — укажите причину (списание станет ручным).
                </p>
              )}
              <Form.Group className="mb-2">
                <Form.Label>Сумма (¥) — списание отрицательная, пополнение положительное</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Причина изменения (обязательно, если сумма отличается от авто-расчёта)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={editReason}
                  onChange={(e) => setEditReason(e.target.value)}
                  placeholder="Например: корректировка по договорённости с клиентом"
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEdit} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner animation="border" size="sm" /> : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={topUpModal}
        onHide={() => {
          setTopUpModal(false);
          setTopUpSelectedUser(null);
          setTopUpUserStores([]);
          setTopUpAmountsByStore({});
          setTopUpError('');
          setTopUpComment('');
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Пополнить аккаунт пользователя</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {topUpError && <Alert variant="danger">{topUpError}</Alert>}
          <Form.Group className="mb-2">
            <Form.Label>Поиск пользователя (email, имя)</Form.Label>
            <Form.Control
              type="text"
              value={topUpUserQuery}
              onChange={(e) => {
                const q = e.target.value;
                setTopUpUserQuery(q);
                if (q.length >= 2) {
                  api.searchUsers(q, 10).then((r) => {
                    setTopUpUserResults(r?.data || []);
                  }).catch(() => setTopUpUserResults([]));
                } else {
                  setTopUpUserResults([]);
                }
              }}
              placeholder="Введите email или имя..."
            />
          </Form.Group>
          {topUpUserResults.length > 0 && (
            <ul className="list-group mb-2" style={{ maxHeight: 200, overflow: 'auto' }}>
              {topUpUserResults.map((u) => (
                <li
                  key={u.id}
                  className={`list-group-item list-group-item-action ${topUpSelectedUser?.id === u.id ? 'active' : ''}`}
                  onClick={() => {
                    setTopUpSelectedUser(u);
                    setTopUpUserStores([]);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  {u.email} {u.first_name || u.last_name ? `(${u.first_name || ''} ${u.last_name || ''})`.trim() : ''}
                </li>
              ))}
            </ul>
          )}
          {topUpSelectedUser && (
            <>
              <p className="small text-muted mb-2">Выбран: {topUpSelectedUser.email}</p>
              {topUpStoresLoading ? (
                <p className="small text-muted mb-2">Загрузка магазинов...</p>
              ) : topUpUserStores.length === 0 ? (
                <Alert variant="warning" className="mb-2">У пользователя нет активных магазинов. Пополнение невозможно.</Alert>
              ) : (
                <>
                  <Form.Label className="d-block mb-1">Сумма пополнения (¥) для каждого магазина:</Form.Label>
                  <div className="mb-2" style={{ maxHeight: 260, overflow: 'auto' }}>
                    {topUpUserStores.map((s) => (
                      <div key={s.id} className="d-flex align-items-center gap-2 mb-2">
                        <div className="flex-grow-1 small text-muted">
                          {s.name ? `${s.name} (ID: ${s.store_id})` : `Store ID: ${s.store_id}`}
                        </div>
                        <Form.Control
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0"
                          style={{ width: 120 }}
                          value={topUpAmountsByStore[String(s.id)] ?? ''}
                          onChange={(e) => setTopUpAmountsByStore((prev) => ({ ...prev, [String(s.id)]: e.target.value }))}
                        />
                        <span className="text-muted">¥</span>
                      </div>
                    ))}
                  </div>
                  {(() => {
                    const withAmount = topUpUserStores.filter((s) => {
                      const v = parseFloat(topUpAmountsByStore[String(s.id)]);
                      return !Number.isNaN(v) && v > 0;
                    });
                    if (withAmount.length > 0) {
                      return (
                        <p className="small text-info mb-2">
                          Будет создано {withAmount.length} пополнений для выбранных магазинов.
                        </p>
                      );
                    }
                    return null;
                  })()}
                  <Form.Group className="mb-0 mt-2">
                    <Form.Label className="small">Комментарий для клиента (необязательно)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={topUpComment}
                      onChange={(e) => setTopUpComment(e.target.value)}
                      placeholder="Только этот текст увидит клиент в разделе Accounts (без служебных пометок)."
                      maxLength={500}
                    />
                    <Form.Text className="text-muted">
                      До 500 символов; один текст для всех пополнений этого нажатия. Пусто — у клиента не будет подписи под строкой пополнения.
                    </Form.Text>
                  </Form.Group>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setTopUpModal(false);
              setTopUpComment('');
            }}
          >
            Отмена
          </Button>
          <Button
            variant="primary"
            disabled={
              !topUpSelectedUser || topUpUserStores.length === 0 || topUpStoresLoading || topUpSubmitting ||
              !topUpUserStores.some((s) => {
                const v = parseFloat(topUpAmountsByStore[String(s.id)]);
                return !Number.isNaN(v) && v > 0;
              })
            }
            onClick={async () => {
              const amountsByStore = {};
              topUpUserStores.forEach((s) => {
                const v = parseFloat(topUpAmountsByStore[String(s.id)]);
                if (!Number.isNaN(v) && v > 0) amountsByStore[String(s.id)] = v;
              });
              if (!topUpSelectedUser || Object.keys(amountsByStore).length === 0) return;
              setTopUpSubmitting(true);
              setTopUpError('');
              try {
                await api.createBillingTopUp(topUpSelectedUser.id, amountsByStore, topUpComment);
                loadExpenses();
                setTopUpModal(false);
                setTopUpComment('');
              } catch (err) {
                setTopUpError(err.message || err.data?.error || 'Ошибка пополнения');
              } finally {
                setTopUpSubmitting(false);
              }
            }}
          >
            {topUpSubmitting ? <Spinner animation="border" size="sm" /> : 'Пополнить'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminBillingsPage;
